import { INTENT_TYPE } from '../../constants';
import { hasOperandEvidence } from '../features/operand-evidence';
import { AUX_WORDS } from '../pack/lexicons';
import { type ConvergenceRule, type FeatureContext } from '../types';

import { getRemainderAfterFirstToken } from './wh-question';

/**
 * `C-AUX-QUESTION` (priority 925) — an auxiliary verb opens a marked question.
 *
 * This grammar is strictly narrower than the wh-question one and requires a terminal
 * question mark, because an auxiliary at token 0 is not distinctive on its own:
 * `is` and `can` and `will` open a very large number of real titles. `Can Rovo
 * search Confluence` is plausibly a page name; `Can Rovo search Confluence?` is a
 * question.
 *
 * NFKC normalization is what makes the terminal test work for both scripts: the
 * full-width `？` folds to `?` before any rule sees it, so a CJK keyboard and a US
 * one behave identically. The `hasNonLatinLetter` guard still applies, so folded
 * punctuation does not smuggle non-English text into an English-only grammar.
 *
 * Since `C-TERMINAL-QUESTION` sits below and claims any question-marked query, this
 * grammar no longer changes a *destination* — everything it claims would reach Chat
 * anyway. It is kept because it changes the reported reason, and per-rule reason
 * distribution is what the catalog exists to produce.

 */
export const matchesAuxQuestion = (features: FeatureContext): boolean => {
	if (features.hasNonLatinLetter || features.terminalPunctuation !== '?') {
		return false;
	}

	const tokens = features.wordTokens;

	if (tokens.length < 2 || !AUX_WORDS.includes(tokens[0].toLowerCase())) {
		return false;
	}

	return hasOperandEvidence(getRemainderAfterFirstToken(features.qMatch, tokens[0]));
};

export const auxQuestionRule: ConvergenceRule = {
	type: INTENT_TYPE.CHAT,
	pattern: 'AUX_QUESTION',
	matches: matchesAuxQuestion,
};
